import React, { useEffect, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Divider, Grid, IconButton, Paper, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useParams, Link } from 'react-router-dom';
import agent from '../../agent';
import useTranslation from '../../customHooks/translations';
import { DEFAULT_THEME_COLOR } from '../../constants/theme';

const StatBox = ({ value, label }) => (
  <Box sx={{ textAlign: 'center', flex: 1 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Box>
);

const FreakItemDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const translation = useTranslation();
  const [item, setItem] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    agent.FreakItems.get(slug)
      .then((res) => setItem(res.freakItem))
      .catch(() => navigate('/404'));
  }, [slug, navigate]);

  if (!item) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
  }

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await agent.FreakItems.del(slug);
      navigate(-1);
    } catch (error) {
      console.error(error);
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      {item.images?.length ? (
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mb: 2 }}>
          {item.images.map((image) => (
            <Box
              key={image.name}
              component="img"
              src={image.imageUrl}
              alt={item.name}
              sx={{ height: { xs: 200, sm: 300 }, borderRadius: 2, maxWidth: '100%' }}
            />
          ))}
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{item.name}</Typography>
          <Typography variant="subtitle1" sx={{ color: DEFAULT_THEME_COLOR }}>
            {[item.brand, item.model, item.year].filter(Boolean).join(' · ')}
          </Typography>
        </Box>
        {item.isOwner && (
          <>
            <IconButton onClick={() => navigate(`/freak-items-editor/${slug}`)}><EditIcon /></IconButton>
            <IconButton onClick={() => setDeleteOpen(true)}><DeleteIcon /></IconButton>
          </>
        )}
      </Box>

      {item.category && <Chip label={item.category.name} size="small" sx={{ mt: 1 }} />}

      <Paper sx={{ display: 'flex', p: 2, mt: 2, borderRadius: 2 }}>
        <StatBox value={item.usageStats.freaksCount} label={translation.freakItems.activities} />
        <StatBox value={`${Number(item.usageStats.totalDistance).toFixed(1)} km`} label={translation.freakItems.distance} />
        <StatBox value={item.usageStats.totalDuration} label={translation.freakItems.time} />
      </Paper>

      {item.specs?.length ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{translation.freakItems.specs}</Typography>
          {item.specs.map((spec, index) => (
            <Box key={index}>
              <Grid container sx={{ py: 1 }}>
                <Grid item xs={5}><Typography variant="body2" sx={{ fontWeight: 600 }}>{spec.key}</Typography></Grid>
                <Grid item xs={7}><Typography variant="body2">{spec.value}</Typography></Grid>
              </Grid>
              <Divider />
            </Box>
          ))}
        </Box>
      ) : null}

      {item.body ? (
        <Box sx={{ mt: 3 }} dangerouslySetInnerHTML={{ __html: item.body }} />
      ) : null}

      {item.freaks?.length ? (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{translation.freakItems.linkedFreaks}</Typography>
          {item.freaks.map((freak) => (
            <Button key={freak.slug} component={Link} to={`/freak/${freak.slug}`} sx={{ display: 'block', textAlign: 'left' }}>
              {freak.title}
            </Button>
          ))}
        </Box>
      ) : null}

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{translation.freakItems.delete}</DialogTitle>
        <DialogContent>
          <Typography>{translation.freakItems.deleteConfirm}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>
            {translation.buttons.cancel}
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            sx={{ bgcolor: '#ff5252', '&:hover': { bgcolor: '#d32f2f' } }}
          >
            {translation.freakItems.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FreakItemDetails;
